/**
 * Third-level flyout: panel mounted inside top-level menu <details> (fixed position)
 * so HoverIntent does not close L2 while pointer is on L3.
 */
const DESKTOP_MQ = '(min-width: 1150px)';
const ROOT_SEL = '.hdt-submenu-child-flyout';
const ROW_SEL = '.hdt-sub-menu-lever-1 > li';
const PANEL_SEL = '.hdt-subflyout-panel';
const GAP = 0;
const HIDE_DELAY = 280;

let active = null;
let hideTimer = null;
let bridgeEl = null;
let lastPointer = { x: 0, y: 0 };
let pickGuardBound = false;
let mainMenuObserver = null;

function isDesktop() {
  return window.matchMedia(DESKTOP_MQ).matches;
}

function isRTL() {
  return document.documentElement.getAttribute('dir') === 'rtl';
}

function isFlyoutMode() {
  return document.querySelector(ROOT_SEL) != null;
}

function getRow(target) {
  return target.closest(`${ROOT_SEL} ${ROW_SEL}`);
}

function getPanel(li) {
  return li?.querySelector(`:scope ${PANEL_SEL}`);
}

function getDropdown(li) {
  return (
    li?.closest('.hdt-dropdown-menu, .hdt-sub-menu.hdt-absolute') ??
    li?.closest('#hdt-nav-ul > li')?.querySelector(
      'hdt-header-menu > details > :is(.hdt-dropdown-menu, .hdt-sub-menu)'
    )
  );
}

function getMainMenu(li) {
  return li?.closest('#hdt-nav-ul > li')?.querySelector('hdt-header-menu') ?? null;
}

function getMainDetails(li) {
  return getMainMenu(li)?.querySelector('details') ?? null;
}

function getRowDetails(li) {
  return li?.querySelector(':scope details');
}

function rememberHome(panel) {
  if (panel.dataset.flyoutHome === '1') return;
  panel._flyoutParent = panel.parentElement;
  panel._flyoutNext = panel.nextSibling;
  panel.dataset.flyoutHome = '1';
}

function restoreHome(panel) {
  if (!panel._flyoutParent) return;
  const { _flyoutParent: parent, _flyoutNext: next } = panel;
  if (next) parent.insertBefore(panel, next);
  else parent.appendChild(panel);
}

/** Mount L3 panel under top-level menu details (not body) — keeps L2 open on hover. */
function mountPanel(panel, li) {
  rememberHome(panel);
  const mountTarget = getMainDetails(li) ?? document.body;
  mountTarget.appendChild(panel);
}

function ensureBridge() {
  if (bridgeEl) return bridgeEl;
  bridgeEl = document.createElement('div');
  bridgeEl.className = 'hdt-subflyout-bridge';
  bridgeEl.setAttribute('aria-hidden', 'true');
  const parent = active?.mainDetails ?? document.body;
  parent.appendChild(bridgeEl);
  bridgeEl.addEventListener('pointerenter', onBridgeEnter);
  bridgeEl.addEventListener('pointerleave', onBridgeLeave);
  return bridgeEl;
}

function removeBridge() {
  if (!bridgeEl) return;
  bridgeEl.remove();
  bridgeEl = null;
}

function placeBridge(dropRect, panelRect) {
  const bridge = ensureBridge();
  const top = Math.min(dropRect.top, panelRect.top);
  const bottom = Math.max(dropRect.bottom, panelRect.bottom);
  const rtl = isRTL();
  let left;
  let width;

  if (rtl) {
    width = Math.max(4, dropRect.left - panelRect.right);
    left = panelRect.right;
  } else {
    width = Math.max(4, panelRect.left - dropRect.right);
    left = dropRect.right;
  }

  bridge.style.cssText = [
    'position:fixed',
    `top:${top}px`,
    `height:${Math.max(bottom - top, 1)}px`,
    `left:${left}px`,
    `width:${width}px`,
    'z-index:10000',
    'pointer-events:auto',
    'background:transparent',
  ].join(';');
}

function place(panel, li) {
  const rowRect = li.getBoundingClientRect();
  const dropdown = getDropdown(li);
  const dropRect = dropdown?.getBoundingClientRect() ?? rowRect;
  const rtl = isRTL();

  panel.style.position = 'fixed';
  panel.style.top = `${rowRect.top}px`;
  panel.style.maxHeight = `${Math.min(window.innerHeight - rowRect.top - 12, window.innerHeight * 0.85)}px`;
  panel.style.overflowY = 'auto';
  panel.style.overflowX = 'hidden';
  panel.style.zIndex = '10001';

  if (rtl) {
    panel.style.left = 'auto';
    panel.style.right = `${window.innerWidth - dropRect.left + GAP}px`;
  } else {
    panel.style.left = `${dropRect.right + GAP}px`;
    panel.style.right = 'auto';
  }

  requestAnimationFrame(() => {
    const pr = panel.getBoundingClientRect();
    if (!rtl && pr.right > window.innerWidth - 8) {
      panel.style.left = `${Math.max(8, dropRect.left - pr.width - GAP)}px`;
    }
    if (rtl && pr.left < 8) {
      panel.style.right = `${window.innerWidth - dropRect.right - pr.width - GAP}px`;
    }
    if (pr.bottom > window.innerHeight - 8) {
      panel.style.top = `${Math.max(8, window.innerHeight - pr.height - 8)}px`;
    }
    const dropRect2 = dropdown?.getBoundingClientRect() ?? dropRect;
    placeBridge(dropRect2, panel.getBoundingClientRect());
  });
}

function openRow(li) {
  const details = getRowDetails(li);
  const accordion = li.querySelector(':scope > hdt-accordion');
  if (!details || details.hasAttribute('open')) return;
  if (accordion?.toggle) accordion.toggle(true);
  else {
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  }
}

function pinMainMenuDirect(mainMenu, mainDetails) {
  if (!mainMenu || !mainDetails) return;
  const summary = mainDetails.querySelector(':scope > summary');
  const content = summary?.nextElementSibling;
  summary?.classList.add('is_action-hover');
  summary?.setAttribute('aria-expanded', 'true');
  if (!mainDetails.hasAttribute('open')) {
    mainDetails.setAttribute('open', '');
    if (!mainMenu.open && typeof mainMenu.toggle === 'function') {
      try {
        mainMenu.toggle(true);
      } catch {
        /* keep attribute open */
      }
    }
  }
  if (content) {
    content.style.opacity = '1';
    content.style.visibility = 'visible';
    content.style.pointerEvents = 'auto';
  }
}

function setFlyoutLock(on) {
  if (on) document.documentElement.setAttribute('data-subflyout-active', '');
  else document.documentElement.removeAttribute('data-subflyout-active');
}

function watchMainDetails(mainDetails, mainMenu) {
  unwatchMainDetails();
  if (!mainDetails) return;
  mainMenuObserver = new MutationObserver(() => {
    if (!active) return;
    if (!mainDetails.hasAttribute('open')) {
      pinMainMenuDirect(mainMenu, mainDetails);
    }
  });
  mainMenuObserver.observe(mainDetails, { attributes: true, attributeFilter: ['open'] });
}

function unwatchMainDetails() {
  mainMenuObserver?.disconnect();
  mainMenuObserver = null;
}

function bindPickGuard() {
  if (pickGuardBound) return;
  pickGuardBound = true;
  document.addEventListener(
    'pick',
    (event) => {
      if (!active) return;
      if (!event.detail?.offClick) return;
      if (!active.mainMenu?.contains(event.target)) return;
      if (isPointerInFlyoutZone()) {
        event.stopImmediatePropagation();
        pinMainMenuDirect(active.mainMenu, active.mainDetails);
      }
    },
    true
  );
}

function isInFlyoutZone(target) {
  if (!active || !target || !(target instanceof Node)) return false;
  const { li, panel, dropdown, mainMenu, mainDetails } = active;
  if (li.contains(target) || panel.contains(target)) return true;
  if (bridgeEl?.contains(target)) return true;
  if (dropdown?.contains(target)) return true;
  if (mainDetails?.contains(target)) return true;
  if (mainMenu?.contains(target)) return true;
  return false;
}

function isPointerInFlyoutZone() {
  if (!active) return false;
  const el = document.elementFromPoint(lastPointer.x, lastPointer.y);
  if (!el) return false;
  return isInFlyoutZone(el);
}

function isHoverInFlyoutZone() {
  if (!active) return false;
  return (
    active.li.matches(':hover') ||
    active.panel.matches(':hover') ||
    active.dropdown?.matches(':hover') ||
    active.mainDetails?.matches(':hover') ||
    bridgeEl?.matches(':hover')
  );
}

function cancelHide() {
  clearTimeout(hideTimer);
  hideTimer = null;
}

function maintainWhileActive() {
  if (!active) return;
  pinMainMenuDirect(active.mainMenu, active.mainDetails);
}

function onBridgeEnter() {
  cancelHide();
  maintainWhileActive();
}

function onBridgeLeave(event) {
  scheduleHide(event.relatedTarget);
}

function bindPanelEvents(panel) {
  if (panel.dataset.flyoutBound === '1') return;
  panel.dataset.flyoutBound = '1';
  panel.addEventListener('pointerenter', () => {
    cancelHide();
    maintainWhileActive();
  });
  panel.addEventListener('pointerleave', (event) => scheduleHide(event.relatedTarget));
}

function show(li) {
  if (!isDesktop() || !isFlyoutMode()) return;
  const panel = getPanel(li);
  if (!panel) return;

  hide();

  const dropdown = getDropdown(li);
  const mainMenu = getMainMenu(li);
  const mainDetails = getMainDetails(li);

  mountPanel(panel, li);
  bindPanelEvents(panel);
  bindPickGuard();

  active = { li, panel, dropdown, mainMenu, mainDetails };
  setFlyoutLock(true);
  watchMainDetails(mainDetails, mainMenu);
  openRow(li);
  pinMainMenuDirect(mainMenu, mainDetails);

  panel.classList.add('is-subflyout-active');
  panel.setAttribute('aria-hidden', 'false');
  place(panel, li);
}

function hide() {
  if (!active) return;
  const { panel } = active;
  panel.classList.remove('is-subflyout-active');
  panel.setAttribute('aria-hidden', 'true');
  panel.style.cssText = '';
  restoreHome(panel);
  removeBridge();
  unwatchMainDetails();
  setFlyoutLock(false);
  active = null;
  cancelHide();
}

function scheduleHide(relatedTarget) {
  cancelHide();
  hideTimer = setTimeout(() => {
    if (!active) return;
    if (isInFlyoutZone(relatedTarget) || isPointerInFlyoutZone() || isHoverInFlyoutZone()) {
      return;
    }
    hide();
  }, HIDE_DELAY);
}

function onPointerOver(event) {
  if (!isDesktop() || !isFlyoutMode()) return;

  const row = getRow(event.target);
  if (!row || !getPanel(row)) return;

  const accordion = row.querySelector(':scope > hdt-accordion');
  if (accordion?.hasAttribute('only-click-icon')) {
    if (!event.target.closest('[accordion-icon]')) return;
  }

  show(row);
}

function onPointerOut(event) {
  if (!isDesktop() || !active) return;
  if (isInFlyoutZone(event.relatedTarget)) return;
  scheduleHide(event.relatedTarget);
}

function onToggle(event) {
  if (!isDesktop() || !isFlyoutMode()) return;
  const details = event.target;
  if (!details.matches(`${ROOT_SEL} ${ROW_SEL} details`)) return;
  const li = details.closest(ROW_SEL);
  if (!li || !getPanel(li)) return;

  if (details.open || details.getAttribute('aria-expanded') === 'true') {
    show(li);
    return;
  }

  if (active?.li === li && (isPointerInFlyoutZone() || isHoverInFlyoutZone())) {
    details.setAttribute('open', '');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    return;
  }

  if (active?.li === li) hide();
}

function bind() {
  document.addEventListener(
    'pointermove',
    (e) => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
      if (active && isPointerInFlyoutZone()) maintainWhileActive();
    },
    { passive: true }
  );
  document.addEventListener('pointerover', onPointerOver, true);
  document.addEventListener('pointerout', onPointerOut, true);
  document.addEventListener('toggle', onToggle, true);
  window.addEventListener('resize', () => active && place(active.panel, active.li));
  window.addEventListener('scroll', () => active && place(active.panel, active.li), true);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bind);
} else {
  bind();
}
